import React, { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { fetchTicketsRelatedCount } from 'flow/actions/tickets-action';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { selectTotalNumberOfRelatedTickets } from 'flow/selectors/TicketsSelectors';
import { TicketKey } from 'flow/types/EntityKeys';
import TabLabel from './TabLabel';

interface Props {
    ticketKey: TicketKey;
}

export const formatRelatedCount = (count: number) =>
    count > 99 ? '99+' : count;

// The Related tab label owns the related-ticket count: it fetches the total
// count and renders it as a badge next to the label.
const RelatedTabLabel = ({ ticketKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const relatedTicketsCount = useAppSelector((state) =>
        selectTotalNumberOfRelatedTickets(state, ticketKey),
    );

    useEffect(() => {
        dispatch(fetchTicketsRelatedCount(ticketKey));
    }, [dispatch, ticketKey]);

    return (
        <span className="inline-flex items-center">
            <TabLabel
                iconKey="history"
                text={i18next.t('tickets.related.title')}
            />
            {/* min-w reserves the count's space while it loads, so the pill
             *  (and everything after it) keeps its width once "(12)" shows. */}
            <span
                className={twMerge(
                    'inline-flex min-w-[4ch] items-center justify-end tabular-nums',
                    relatedTicketsCount !== undefined &&
                        relatedTicketsCount > 99 &&
                        'ms-1',
                )}
            >
                {relatedTicketsCount !== undefined ? (
                    <>
                        {'('}
                        {formatRelatedCount(relatedTicketsCount)}
                        {')'}
                    </>
                ) : (
                    <>
                        {'('}
                        <span className="animate-fade mx-[1px] inline-block h-2 w-2 rounded-sm bg-current opacity-40" />
                        {')'}
                    </>
                )}
            </span>
        </span>
    );
};

export default RelatedTabLabel;
